/**
 * Teaching pearls for ChartMind's Pearls step, from the chartmind-pearls prompt (originally
 * ChartMind V2's, copied from MinusOne's DecisionPoint Quick Draft pearls).
 *
 * ⚠️ THE PROMPT DOC MAY NOT EXIST YET on a given environment; it is seeded by
 * scripts/seedChartMindPrompts.js. Until it is, the call fails with a missing-prompt error and
 * ChartMind hides the step rather than showing one that can only ever look broken.
 */
export const PEARLS_PROMPT_ID = "chartmind-pearls";

/** The error callLLMAPI throws when a prompt has not been set up on this environment. */
export const isMissingPromptError = (err) => /No prompt content is available/i.test(err?.message || "");

function pearlFromString(text, index, diagnosis = "") {
  const body = String(text || "").trim();
  if (!body) return null;
  return {
    id: `pearl-${index}`,
    title: "",
    body,
    diagnosis: String(diagnosis || "").trim(),
  };
}

function pearlFromObject(item, index, fallbackDiagnosis = "") {
  if (!item || typeof item !== "object") return null;
  const body = String(item.body || item.pearl || item.text || "").trim();
  if (!body) return null;
  return {
    id: String(item.id || `pearl-${index}`),
    title: String(item.title || item.topic || "").trim(),
    body,
    diagnosis: String(
      item.diagnosis || item.condition || item.subject || fallbackDiagnosis || "",
    ).trim(),
  };
}

/**
 * Flatten LLM output into pearl rows. Supports:
 * - `["pearl", ...]`
 * - `[{ title, body }, ...]` / `[{ diagnosis, body }, ...]`
 * - `[{ diagnosis, pearls: ["…"] }, …]` (Events working differential)
 * - `{ pearls: [...] }` wrappers of any of the above
 */
export function parsePearls(content) {
  if (!content) return [];
  const trimmed = String(content).trim();
  if (!trimmed.includes("{") && !trimmed.includes("[")) return [];

  let jsonStr = trimmed;
  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fence) jsonStr = fence[1];

  let parsed;
  try {
    parsed = JSON.parse(jsonStr.trim());
  } catch {
    return [];
  }

  const list = Array.isArray(parsed) ? parsed : parsed?.pearls;
  if (!Array.isArray(list)) return [];

  const out = [];
  list.forEach((item, index) => {
    if (typeof item === "string") {
      const pearl = pearlFromString(item, out.length);
      if (pearl) out.push(pearl);
      return;
    }
    if (!item || typeof item !== "object") return;

    const diagnosis = String(item.diagnosis || item.condition || item.subject || "").trim();
    const nested = Array.isArray(item.pearls) ? item.pearls : null;
    if (nested) {
      nested.forEach((entry) => {
        if (typeof entry === "string") {
          const pearl = pearlFromString(entry, out.length, diagnosis);
          if (pearl) out.push(pearl);
          return;
        }
        const pearl = pearlFromObject(entry, out.length, diagnosis);
        if (pearl) out.push(pearl);
      });
      return;
    }

    const pearl = pearlFromObject(item, out.length, diagnosis);
    if (pearl) out.push(pearl);
  });

  return out;
}

/**
 * Group pearls under their diagnosis for the Pearls step. Pearls with no diagnosis stay in one
 * untitled group (legacy single-subject prompts).
 */
export function groupPearlsByDiagnosis(pearls = []) {
  const groups = [];
  const indexByKey = new Map();
  pearls.forEach((pearl) => {
    const key = String(pearl?.diagnosis || "").trim();
    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({ diagnosis: key, pearls: [] });
    }
    groups[indexByKey.get(key)].pearls.push(pearl);
  });
  return groups;
}

/**
 * @returns {Promise<Array<{id: string, title: string, body: string, diagnosis: string}>>}
 */
export async function generatePearls({
  subject,
  organizationId = null,
  promptId = PEARLS_PROMPT_ID,
  fallbackPromptId,
  focus,
  patientContext = null,
  // Events note type: documented events that frame the working differential.
  clinicalContext = "",
}) {
  const text = String(subject || "").trim();
  if (!text) return [];

  const context = String(clinicalContext || "").trim();
  const userMessage = context
    ? `${context}\n\nCLINICAL SUBJECT:\n\n${text}`
    : `CLINICAL SUBJECT:\n\n${text}`;

  // Dynamic import so PearlsStep can reuse parse/group helpers without loading Firebase.
  const { invokeLLMPrompt } = await import("services/llmService");
  const result = await invokeLLMPrompt({
    promptId,
    fallbackPromptId,
    focus,
    userMessage,
    organizationId: organizationId || undefined,
    patientContext,
  });

  return parsePearls(result?.output);
}
